import React, { useEffect, useState, useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Box, Inline, Text } from '@atlaskit/primitives/compiled';

import { GeneratingStreamAnimation } from '@atlassian/ai-components';

export type AudioTimeIndicatorVariants = 'default' | 'blue';

/**
 * TODO: This component should be refactored to become more modular and reusable via Inversion of Control
 * this means removing isLoading and hasFetched from the props and instead passing in direct props to the component
 * such as shouldShowLoadingState, shouldShowGenerateCTA, etc, and having the consumer decide how to derive these things
 * Also means removing the default value for hasFetched
 * https://hello.jira.atlassian.cloud/browse/CCI-4387
 */

type AudioTimeIndicatorProps = {
	audioRef: React.RefObject<HTMLAudioElement>;
	isLoading: boolean;
	hasFetched?: boolean;
	variant?: AudioTimeIndicatorVariants;
};

type TextWrapperProps = {
	children: React.ReactNode;
	variant?: AudioTimeIndicatorVariants;
};

const DEFAULT_TIME_DISPLAY = '0:00';

// Extra padding ensures there is no spacing change when transitioning to/from the generating animation
const PaddedBox = ({ children }: { children: React.ReactNode }) => (
	<Box paddingBlock="space.025">{children}</Box>
);

const i18n = defineMessages({
	generatingAudioAnimationAlt: {
		id: 'audio.audio-time-indicator.generating-alt',
		defaultMessage: 'Generating',
		description: 'Alt text of the generation animation',
	},
	loadingStateMessage: {
		id: 'audio.audio-time-indicator.loading-state-message',
		defaultMessage: 'Generating briefing...',
		description:
			'The message in our audio time indicator when we are generating the audio briefing, and do not have time information to show.',
	},
	generateAudioMessage: {
		id: 'audio.audio-time-indicator.generate-audio-message',
		defaultMessage: 'Press play to generate a briefing',
		description:
			'The message in our audio time indicator prompting the user to press play to generate a briefing',
	},
});

const formatTime = (timeInSeconds: number): string => {
	const minutes = Math.floor(timeInSeconds / 60);
	const seconds = Math.floor(timeInSeconds % 60);
	return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const TextWrapper = ({ children, variant }: TextWrapperProps) => {
	if (variant === 'blue') {
		return (
			<Text color="color.text.selected" weight="medium">
				{children}
			</Text>
		);
	}

	return (
		<Text color="color.text.subtle" weight="regular">
			{children}
		</Text>
	);
};

const DividerWrapper = ({ children, variant }: TextWrapperProps) => {
	if (variant === 'blue') {
		return (
			<Text color="color.text.disabled" weight="semibold">
				{children}
			</Text>
		);
	}

	return (
		<Text color="color.text.subtlest" weight="medium">
			{children}
		</Text>
	);
};

const AudioStatus = ({ hasFetched }: { hasFetched: boolean }) => {
	const { formatMessage } = useIntl();
	const message = !hasFetched ? i18n.generateAudioMessage : i18n.loadingStateMessage;

	return (
		<PaddedBox>
			<Text color="color.text.subtle">{formatMessage(message)}</Text>
		</PaddedBox>
	);
};

export const AudioTimeIndicator = ({
	audioRef,
	isLoading,
	hasFetched = true,
	variant = 'default',
}: AudioTimeIndicatorProps) => {
	const { formatMessage } = useIntl();

	const [currentTimeDisplay, setCurrentTimeDisplay] = useState(DEFAULT_TIME_DISPLAY);
	const [totalTimeDisplay, setTotalTimeDisplay] = useState(DEFAULT_TIME_DISPLAY);

	const handleTimeUpdate = useCallback(() => {
		const audioEl = audioRef.current;
		if (!audioEl) return;

		const currentTime = Math.floor(audioEl.currentTime ?? 0);
		setCurrentTimeDisplay(formatTime(currentTime));
	}, [audioRef]);

	const handleDurationChange = useCallback(() => {
		const audioEl = audioRef.current;
		if (!audioEl) return;

		if (audioEl.duration !== Infinity) {
			setTotalTimeDisplay(formatTime(audioEl.duration));
		} else {
			setTotalTimeDisplay(DEFAULT_TIME_DISPLAY);
		}
	}, [audioRef]);

	useEffect(() => {
		const audioEl = audioRef.current;
		if (!audioEl) {
			setCurrentTimeDisplay(DEFAULT_TIME_DISPLAY);
			setTotalTimeDisplay(DEFAULT_TIME_DISPLAY);
			return;
		}

		audioEl.addEventListener('durationchange', handleDurationChange);
		audioEl.addEventListener('timeupdate', handleTimeUpdate);

		return () => {
			audioEl.removeEventListener('durationchange', handleDurationChange);
			audioEl.removeEventListener('timeupdate', handleTimeUpdate);
		};
	}, [audioRef, handleTimeUpdate, handleDurationChange]);

	const isAudioDurationUnknown = isLoading || totalTimeDisplay === DEFAULT_TIME_DISPLAY;

	if (isLoading || !hasFetched) {
		return <AudioStatus hasFetched={hasFetched} />;
	}

	return (
		<Box testId="audio-time-indicator">
			<Inline alignInline="center" space="space.100">
				<PaddedBox>
					<TextWrapper variant={variant}>
						{isLoading ? DEFAULT_TIME_DISPLAY : currentTimeDisplay}
					</TextWrapper>
				</PaddedBox>
				<PaddedBox>
					<DividerWrapper variant={variant}>/</DividerWrapper>
				</PaddedBox>
				{isAudioDurationUnknown ? (
					<GeneratingStreamAnimation alt={formatMessage(i18n.generatingAudioAnimationAlt)} />
				) : (
					<PaddedBox>
						<TextWrapper variant={variant}>{totalTimeDisplay}</TextWrapper>
					</PaddedBox>
				)}
			</Inline>
		</Box>
	);
};
